import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCcw, Trash2, CheckCircle2, AlertCircle, Search, Activity } from "lucide-react";
import { formatDateTime } from "@/lib/dates";

export default function ApiLogsTab() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, success: 0, errors: 0 });
  const [loading, setLoading] = useState(true);
  const [filterPath, setFilterPath] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPath) params.path = filterPath;
      if (filterStatus !== "all") params.status = filterStatus;
      const res = await api.get("/admin/api-logs", { params });
      setLogs(res.data.logs || []);
      setStats(res.data.stats || {});
    } catch (err) {
      toast.error("Errore caricamento storico API");
    } finally {
      setLoading(false);
    }
  }, [filterPath, filterStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const clearAll = async () => {
    if (!window.confirm("Cancellare tutto lo storico API? L'azione è irreversibile.")) return;
    try {
      await api.delete("/admin/api-logs");
      toast.success("Storico cancellato");
      load();
    } catch {
      toast.error("Errore cancellazione");
    }
  };

  return (
    <section data-testid="api-logs-tab">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <StatCard
          icon={<Activity className="w-4 h-4" />}
          label="Totale chiamate"
          value={stats.total}
          tone="bg-[#1E3A5F] text-white"
        />
        <StatCard
          icon={<CheckCircle2 className="w-4 h-4" />}
          label="Successo (2xx/3xx)"
          value={stats.success}
          tone="bg-emerald-50 text-emerald-900 border border-emerald-200"
        />
        <StatCard
          icon={<AlertCircle className="w-4 h-4" />}
          label="Errori (4xx/5xx)"
          value={stats.errors}
          tone="bg-rose-50 text-rose-900 border border-rose-200"
        />
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#635F59]" />
          <Input
            placeholder="Filtra per path (es. bookings)"
            value={filterPath}
            onChange={(e) => setFilterPath(e.target.value)}
            data-testid="api-logs-filter-path"
            className="pl-9 h-10"
          />
        </div>
        <div className="w-44">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-10" data-testid="api-logs-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="success">Successo</SelectItem>
              <SelectItem value="error">Errori</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          data-testid="api-logs-refresh"
          className="bg-[#1E3A5F] hover:bg-[#152A45] text-white"
        >
          <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
        <Button
          onClick={clearAll}
          variant="outline"
          data-testid="api-logs-clear"
          className="text-rose-700 border-rose-200 hover:bg-rose-50"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Pulisci
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#FDFBF7] border-b border-[#E8E2D9] text-xs uppercase tracking-wider text-[#635F59]">
              <tr>
                <th className="px-4 py-3 text-left">Quando</th>
                <th className="px-4 py-3 text-left">Metodo</th>
                <th className="px-4 py-3 text-left">Endpoint</th>
                <th className="px-4 py-3 text-center">Esito</th>
                <th className="px-4 py-3 text-left">Dettagli</th>
                <th className="px-4 py-3 text-left">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F2EEE7]">
              {logs.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-[#635F59]">
                    Nessuna chiamata registrata.
                  </td>
                </tr>
              )}
              {logs.map((l) => (
                <tr key={l.id} className="hover:bg-[#FDFBF7]/50" data-testid={`api-log-${l.id}`}>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{formatDateTime(l.timestamp)}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className={`inline-block px-2 py-0.5 rounded font-mono font-semibold ${
                      l.method === "POST" ? "bg-violet-100 text-violet-800" :
                      l.method === "DELETE" ? "bg-rose-100 text-rose-800" :
                      "bg-sky-100 text-sky-800"
                    }`}>{l.method}</span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-[#1E3A5F]">{l.path}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-xs font-bold ${
                      l.status_code < 400 ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                    }`}>
                      {l.status_code < 400 ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      {l.status_code}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#635F59] max-w-md truncate" title={l.summary || l.error}>
                    {l.error ? <span className="text-rose-700">{l.error}</span> : l.summary}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-[#635F59]">{l.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon, label, value, tone }) {
  return (
    <div className={`rounded-xl p-4 ${tone}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-3xl font-display mt-1">{value ?? "—"}</div>
    </div>
  );
}
